import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useGoogleLogin } from '@react-oauth/google';
import '../styles/responsive.css';

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M15.68 8.18c0-.57-.05-1.11-.14-1.64H8v3.1h4.3a3.67 3.67 0 01-1.59 2.4v2h2.57c1.51-1.39 2.4-3.44 2.4-5.86z" fill="#4285F4"/>
    <path d="M8 16c2.16 0 3.97-.72 5.3-1.94l-2.57-2a4.8 4.8 0 01-7.16-2.52H.96v2.06A8 8 0 008 16z" fill="#34A853"/>
    <path d="M3.57 9.54A4.8 4.8 0 013.32 8c0-.53.09-1.05.25-1.54V4.4H.96A8 8 0 000 8c0 1.3.31 2.52.96 3.6l2.61-2.06z" fill="#FBBC05"/>
    <path d="M8 3.2a4.33 4.33 0 013.06 1.2l2.3-2.3A7.7 7.7 0 008 0 8 8 0 00.96 4.4l2.61 2.06A4.77 4.77 0 018 3.2z" fill="#EA4335"/>
  </svg>
);

/* Isolated component so useGoogleLogin hook only mounts inside GoogleOAuthProvider */
const GoogleLoginButton: React.FC<{
  onSuccess: (accessToken: string) => void;
  onError: () => void;
  disabled?: boolean;
}> = ({ onSuccess, onError, disabled }) => {
  const login = useGoogleLogin({
    onSuccess: (r) => onSuccess(r.access_token),
    onError,
  });
  return (
    <button onClick={() => login()} disabled={disabled}
      style={{
        width: '100%', padding: '11px 0', background: 'transparent',
        border: `1px solid ${BR}`, borderRadius: 8, color: T1,
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 10, marginBottom: 0,
        transition: 'border-color .18s, background .18s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.background = C2; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = BR; e.currentTarget.style.background = 'transparent'; }}>
      <GoogleIcon /> Continue with Google
    </button>
  );
};

interface LoginPageProps {
  onLoginSuccess: (user: any) => void;
}

/* ── Design tokens */
const BG = '#0a0a0a';
const C1 = '#111111';
const C2 = '#161616';
const BR = '#1e1e1e';
const A  = '#4EDEA3';
const T1 = '#f0f0f0';
const T2 = '#b0b0b0';
const T3 = '#909090';

type Mode = 'login' | 'forgot' | 'forgot-sent';

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [mode,         setMode        ] = useState<Mode>('login');
  const [email,        setEmail       ] = useState('');
  const [password,     setPassword    ] = useState('');
  const [forgotEmail,  setForgotEmail ] = useState('');
  const [error,        setError       ] = useState('');
  const [loading,      setLoading     ] = useState(false);
  const [focused,      setFocused     ] = useState<string | null>(null);

  const inputStyle = (field: string): React.CSSProperties => ({
    width: '100%', padding: '11px 14px',
    background: C2, border: `1px solid ${focused === field ? A : BR}`,
    borderRadius: 8, color: T1, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', transition: 'border-color .18s', boxSizing: 'border-box',
  });

  /* ── Email/password login */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await axios.post('/api/users/login', { email, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.removeItem('elabs_active_tab'); // always land on overview after fresh login
      onLoginSuccess(res.data.user);
      navigate('/intelligence');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally { setLoading(false); }
  };

  /* ── Forgot password */
  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await axios.post('/api/users/forgot-password', { email: forgotEmail });
      setMode('forgot-sent');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  /* ── Google OAuth */
  const handleGoogleSuccess = async (accessToken: string) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post('/api/users/auth/google', { access_token: accessToken });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.removeItem('elabs_active_tab'); // always land on overview after fresh login
      onLoginSuccess(res.data.user);
      navigate('/intelligence');
    } catch (err: any) {
      if (!err.response) {
        setError('Could not reach the server. Make sure the backend is running.');
      } else {
        setError(err.response.data?.error || `Google sign-in failed (${err.response.status}).`);
      }
    } finally { setLoading(false); }
  };

  const hasGoogle = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <div style={{
      background: BG, minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'DM Sans',-apple-system,sans-serif",
    }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: '100%', maxWidth: 400 }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <p style={{
            fontFamily: "'Space Grotesk',sans-serif", fontSize: 22,
            fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 4px',
          }}>Electa Labs</p>
          <p style={{ fontSize: 11, color: A, fontWeight: 700, letterSpacing: '0.12em', margin: '0 0 14px' }}>
            SIGNAL · NOT NOISE
          </p>
          <Link to="/" style={{
            fontSize: 12, color: T3, textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 5,
            transition: 'color .15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = T2; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = T3; }}>
            ← Back to homepage
          </Link>
        </div>

        {/* Card */}
        <div style={{
          background: C1, border: `1px solid ${BR}`,
          borderRadius: 16, padding: '32px 28px', overflow: 'hidden',
        }}>
          <AnimatePresence mode="wait">

            {/* ── Login form */}
            {mode === 'login' && (
              <motion.div key="login"
                initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.2 }}>

                <h1 style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontSize: 22,
                  fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 4px',
                }}>Welcome back</h1>
                <p style={{ fontSize: 13, color: T3, margin: '0 0 24px' }}>
                  Sign in to your Electa Labs account.
                </p>

                {/* Error */}
                {error && (
                  <div style={{
                    marginBottom: 16, padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)',
                    color: '#f87171', fontSize: 13,
                  }}>{error}</div>
                )}

                <form onSubmit={handleLogin}>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T3, letterSpacing: '0.08em', marginBottom: 6 }}>EMAIL</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      required placeholder="you@example.com"
                      onFocus={() => setFocused('email')} onBlur={() => setFocused(null)}
                      style={inputStyle('email')} />
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T3, letterSpacing: '0.08em', marginBottom: 6 }}>PASSWORD</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                      required placeholder="••••••••"
                      onFocus={() => setFocused('password')} onBlur={() => setFocused(null)}
                      style={inputStyle('password')} />
                  </div>

                  {/* Forgot link */}
                  <div style={{ textAlign: 'right', marginBottom: 22 }}>
                    <button type="button"
                      onClick={() => { setMode('forgot'); setError(''); setForgotEmail(email); }}
                      style={{ background: 'none', border: 'none', fontSize: 12, color: T3, cursor: 'pointer', fontFamily: 'inherit', padding: 0, transition: 'color .18s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = A; }}
                      onMouseLeave={e => { e.currentTarget.style.color = T3; }}>
                      Forgot password?
                    </button>
                  </div>

                  <button type="submit" disabled={loading} style={{
                    width: '100%', padding: '12px 0', background: A, color: '#000',
                    border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 800,
                    cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                    opacity: loading ? 0.7 : 1, transition: 'opacity .18s',
                  }}>
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>

                {/* Google button — below the main form */}
                {hasGoogle && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                      <div style={{ flex: 1, height: 1, background: BR }} />
                      <span style={{ fontSize: 11, color: T3, fontWeight: 600, letterSpacing: '0.06em' }}>OR</span>
                      <div style={{ flex: 1, height: 1, background: BR }} />
                    </div>
                    <GoogleLoginButton
                      onSuccess={handleGoogleSuccess}
                      onError={() => setError('Google sign-in failed. Please try again.')}
                      disabled={loading}
                    />
                  </>
                )}

                <p style={{ fontSize: 13, color: T3, textAlign: 'center', margin: '20px 0 0' }}>
                  No account?{' '}
                  <Link to="/register" style={{ color: A, textDecoration: 'none', fontWeight: 600 }}>Create one</Link>
                </p>
              </motion.div>
            )}

            {/* ── Forgot password form */}
            {mode === 'forgot' && (
              <motion.div key="forgot"
                initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>

                <button
                  onClick={() => { setMode('login'); setError(''); }}
                  style={{ background: 'none', border: 'none', fontSize: 12, color: T3, cursor: 'pointer', fontFamily: 'inherit', padding: '0 0 18px', display: 'flex', alignItems: 'center', gap: 6, transition: 'color .18s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = T1; }}
                  onMouseLeave={e => { e.currentTarget.style.color = T3; }}>
                  ← Back to sign in
                </button>

                <h1 style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontSize: 22,
                  fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 4px',
                }}>Reset password</h1>
                <p style={{ fontSize: 13, color: T3, margin: '0 0 24px' }}>
                  Enter your email and we'll send a reset link.
                </p>

                {error && (
                  <div style={{
                    marginBottom: 16, padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)',
                    color: '#f87171', fontSize: 13,
                  }}>{error}</div>
                )}

                <form onSubmit={handleForgot}>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T3, letterSpacing: '0.08em', marginBottom: 6 }}>EMAIL</label>
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                      required placeholder="you@example.com" autoFocus
                      onFocus={() => setFocused('forgot-email')} onBlur={() => setFocused(null)}
                      style={inputStyle('forgot-email')} />
                  </div>
                  <button type="submit" disabled={loading} style={{
                    width: '100%', padding: '12px 0', background: A, color: '#000',
                    border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 800,
                    cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                    opacity: loading ? 0.7 : 1, transition: 'opacity .18s',
                  }}>
                    {loading ? 'Sending…' : 'Send reset link'}
                  </button>
                </form>
              </motion.div>
            )}

            {/* ── Sent confirmation */}
            {mode === 'forgot-sent' && (
              <motion.div key="sent"
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}>
                <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
                  <h1 style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontSize: 20,
                    fontWeight: 800, color: '#fff', margin: '0 0 10px',
                  }}>Check your inbox</h1>
                  <p style={{ fontSize: 13, color: T2, margin: '0 0 28px', lineHeight: 1.7 }}>
                    We sent a reset link to<br />
                    <strong style={{ color: T1 }}>{forgotEmail}</strong>.<br />
                    The link expires in 1 hour.
                  </p>
                  <button
                    onClick={() => { setMode('login'); setError(''); }}
                    style={{
                      background: 'none', border: `1px solid ${BR}`, borderRadius: 8,
                      padding: '10px 24px', fontSize: 13, fontWeight: 600, color: T2,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color .18s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#333'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = BR; }}>
                    Back to sign in
                  </button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
